import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { Util } from '@common/utils/util';
import { ObjectProperties } from '@src/suite/common/data-models/service/object-properties';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { ServiceRecord } from '@suite/common/data-models/service/service-record';

@Injectable({ providedIn: 'root' })
export class TopicService {
    constructor(public nomWebServicesJson: NomWebServicesJson) {}

    public async getTopicContent(database: string, path: string, guid: string): Promise<ServiceResponse<StorytellingTopicRecord>> {
        // get topic
        path = decodeURIComponent(path);
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get('/api/TopicContent', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    database,
                    path,
                    guid,
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to get topic content',
                detailMessage: `Unable to get topic content. \n\rStackTrace: TopicService.getTopicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingTopicRecord>(false, `Unable to get topic content ${error.toString()}`);
        }
        const serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();
        const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceRecord);
        // await DataStore.subscribeForRecordUpdatesV2(converted);
        return new ServiceResponse<StorytellingTopicRecord>(true, converted);
    }

    public async getTopicStories(database: string, path: string, guid: string): Promise<ServiceResponse<StorytellingStoryRecord[]>> {
        // get topic stories
        path = decodeURIComponent(path);
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get('/api/TopicContent/Stories', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    database,
                    path,
                    guid,
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to get topic content',
                detailMessage: `Unable to get topic content. \n\rStackTrace: TopicService.getTopicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingStoryRecord[]>(false, `Unable to get topic content ${error.toString()}`);
        }
        const stories: StorytellingStoryRecord[] = [];
        if (response?.body && Array.isArray(response.body) && response.body.length > 0) {
            for (const story of response.body) {
                const serviceRecord = ServiceRecord.instance(story);
                const converted = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingStoryRecord>(serviceRecord);
                // await DataStore.subscribeForRecordUpdatesV2(converted);
                stories.push(converted);
            }
        }
        return new ServiceResponse<StorytellingStoryRecord[]>(true, stories);
    }
    public async createTopicContent(
        database: string,
        path: string,
        copyFromId: string,
        copyFromPath: string,
        linkToParent: boolean,
        fields: { [id: string]: any },
        children: Array<{ [id: string]: any }>,
        stories: Array<{ [id: string]: any }>,
        title?: string,
        additionalParams?: { [id: string]: any }
    ): Promise<ServiceResponse<StorytellingTopicRecord>> {
        // create topic
        const childrenToSend: Array<any> = new Array<any>();
        const storiesToSend: Array<any> = new Array<any>();
        let response: HttpResponse<any> = null;
        if (fields) {
            Util.escapeSpecialCharsForFileName(fields);
            fields.CreatedBy = `CreatedBy.${this.nomWebServicesJson.UserId}`;
        }
        try {
            if (children && children.length > 0) {
                for (let i = 0; i < children.length; i++) {
                    Util.escapeSpecialCharsForFileName(children[i]);
                    children[i].CreatedBy = `CreatedBy.${this.nomWebServicesJson.UserId}`;
                    childrenToSend.push(this.nomWebServicesJson.convertor.toENPSFields(children[i]));
                }
            }
            if (stories && stories.length > 0) {
                for (let i = 0; i < stories.length; i++) {
                    Util.escapeSpecialCharsForFileName(stories[i]);
                    stories[i].CreatedBy = `CreatedBy.${this.nomWebServicesJson.UserId}`;
                    storiesToSend.push(this.nomWebServicesJson.convertor.toENPSFields(stories[i]));
                }
            }
            const requestParams = {
                database,
                path,
                dateTime: '',
                user: this.nomWebServicesJson.UserId,
                fieldsToUpdate: this.nomWebServicesJson.convertor.toENPSFields(fields),
                children: childrenToSend,
                stories: storiesToSend,
                templateGuid: copyFromId,
                templateProgramId: copyFromPath,
                linkToParent,
                wireDatabase: additionalParams ? additionalParams.wireDatabase : null,
                wirePath: additionalParams ? additionalParams.wirePath : null,
                wireGuid: additionalParams ? additionalParams.wireGuid : null,
                discoverItemDatabase: additionalParams ? additionalParams.discoverItemDatabase : null,
                discoverItemPath: additionalParams ? additionalParams.discoverItemPath : null,
                discoverItemGuid: additionalParams ? additionalParams.discoverItemGuid : null,
                title,
            };
            response = await this.nomWebServicesJson.put('/api/TopicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create topic',
                detailMessage: `Unable to create topic. \n\rStackTrace: TopicService.createTopicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingTopicRecord>(false, `Unable to create topic ${error.toString()}`);
        }
        const serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();

        if (path.endsWith('\\L')) {
            // template
            const topicTemplateRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceRecord);
            return new ServiceResponse<StorytellingTopicRecord>(true, topicTemplateRecord);
        }
        const topicRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceRecord);
        // await DataStore.setCollection(topicRecord, true);
        // const topic = DataStore.byGuid<StorytellingTopicRecord>(topicRecord.guid);
        // topic.links = [];
        return new ServiceResponse<StorytellingTopicRecord>(true, topicRecord);
    }

    public async getTopicTemplateContent(database: string, path: string, guid: string): Promise<ServiceResponse<StorytellingTopicRecord>> {
        let response: HttpResponse<any> = null;
        // let searchParams = this.nomWebServicesJson.searchParams({
        //     database: database,
        //     path: path,
        //     guid: guid
        // });
        try {
            response = await this.nomWebServicesJson.get('/api/TopicContent', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    database,
                    path,
                    guid,
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to get topic content',
            });
            return new ServiceResponse<StorytellingTopicRecord>(false, `Unable to get topic content ${error.toString()}`);
        }
        const serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();
        const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceRecord);
        // await DataStore.setCollection(converted, true);
        return new ServiceResponse<StorytellingTopicRecord>(true, converted);
    }

    public async insertIntoTopicContentV2<T extends StorytellingRecord>(
        database: string,
        collectionPath: string,
        collectionGuid: string,
        insertBeforeGuid: string,
        fields: { [id: string]: any },
        returnText: boolean,
        returnTextLevel: string,
        linkToParent: boolean
    ): Promise<ServiceResponse<T>> {
        // insert assignment
        let response: HttpResponse<any> = null;
        const objectType = fields.Type;
        try {
            Util.escapeSpecialCharsForFileName(fields);
            const requestParams = {
                database,
                collectionPath,
                collectionGuid,
                insertBeforeGuid,
                fieldsToUpdate: this.nomWebServicesJson.convertor.toENPSFields(fields),
                returnText,
                returnTextLevel,
                linkToParent,
            };
            // Assignment or Script (Story)
            response = await this.nomWebServicesJson.patch('/api/TopicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create assignment',
                detailMessage: `Unable to create assignment. \n\rStackTrace: TopicService.insertIntoTopicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to create ${objectType === ObjectTypes.SCRIPT ? 'story' : 'assignment'} ${error.toString()}`);
        }

        if (objectType === ObjectTypes.ASSIGNMENT) {
            if (response.body['Stories']) {
                const objectProperties = new ObjectProperties(response.body.Stories[0]);
                const record = initializeRecord<T>(objectType);
                this.nomWebServicesJson.convertor.deserializeV2(record, objectProperties);
                // await DataStore.subscribeForRecordUpdatesV2(record);
                return new ServiceResponse<T>(true, record);
            }
        } else if (objectType === ObjectTypes.SCRIPT) {
            if (response?.body && Array.isArray(response.body) && response.body.length > 0) {
                const story = response.body[0];
                const serviceRecord = ServiceRecord.instance(story);
                const record = this.nomWebServicesJson.convertor.fromServiceRecordV2<T>(serviceRecord);
                // await DataStore.subscribeForRecordUpdatesV2(record);
                return new ServiceResponse<T>(true, record);
            }
        }
        return new ServiceResponse<T>(false, `Unexpected response in create ${objectType === ObjectTypes.SCRIPT ? 'story' : 'assignment'} call${response.body.toString()}`);
    }

    public async insertTemplateIntoTopicContent(
        database: string,
        collectionPath: string,
        collectionGuid: string,
        fields: { [id: string]: any },
        linkToParent: boolean,
        templateProgramId: string,
        templateGuids: Array<string>
    ): Promise<ServiceResponse<Array<StorytellingStoryRecord>>> {
        // insert assignment
        let response: HttpResponse<any> = null;
        Util.escapeSpecialCharsForFileName(fields);
        const requestParams = {
            database,
            collectionPath,
            collectionGuid,
            fieldsToUpdate: this.nomWebServicesJson.convertor.toENPSFields(fields),
            linkToParent,
            templateProgramId,
        };
        if (templateGuids?.length > 0) {
            requestParams['templateGuids'] = templateGuids;
        }
        // Assignment or Script (Story)
        const objectType = fields.Type;
        try {
            response = await this.nomWebServicesJson.patch('/api/TopicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create assignment',
                detailMessage: `Unable to create assignment. \n\rStackTrace: TopicService.insertIntoTopicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<Array<StorytellingStoryRecord>>(false, `Unable to create ${objectType === ObjectTypes.SCRIPT ? 'story' : 'assignment'} ${error.toString()}`);
        }

        if (response?.body && Array.isArray(response.body) && response.body.length > 0) {
            const res: Array<StorytellingStoryRecord> = [];

            for (const story of response.body) {
                const serviceRecord = ServiceRecord.instance(story);
                const record = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingStoryRecord>(serviceRecord);
                // await DataStore.set(record);
                res.push(record);
            }

            return new ServiceResponse<Array<StorytellingStoryRecord>>(true, res);
        }

        return new ServiceResponse<Array<StorytellingStoryRecord>>(
            false,
            `Unexpected response in create ${objectType === ObjectTypes.SCRIPT ? 'story' : 'assignment'} call${response.body.toString()}`
        );
    }
}
